import { escapeHtml } from '../../../../shared/utils/escapeHtml';
import React, { useMemo } from 'react';
import './InsightsTrendChart.scss';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { getChartThemeColors } from '../../../../shared/utils/chartTheme';
import { buildBucketLabelFormatters } from '../../../../shared/helpers/date/bucketDateLabels';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';
import { PeriodType } from '../../../../shared/types/prompt/PeriodType';

export interface InsightsTrendSeries {
  name: string;
  /** Solid hex: legend dot, line stroke and tooltip marker; the area gradient derives from it. */
  color: string;
  /** Alpha at the top of the area gradient under the line (fades to 0 at the bottom); 0 = line only. */
  areaAlpha: number;
  /** One percentage (0–100) per bucket; null = no data point (skipped in the tooltip). */
  data: Array<number | null>;
}

interface InsightsTrendChartProps {
  /** Buckets in X-axis order; only the bucket start date is read (label format follows the period). */
  buckets: ReadonlyArray<{ date: string }>;
  period: PeriodType;
  /** Series in legend/tooltip order; the parent memoizes it (the options memo depends on it). */
  series: InsightsTrendSeries[];
}

const withAlpha = (hex: string, alpha: number): string => {
  const value = parseInt(hex.slice(1), 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

/**
 * Chart body shared by the Insights trend sections ("Visibility vs. cited over time", "Funnel over
 * time"): percentages per time bucket on a Y axis tightened to the data (as "Brand Score & Visibility"
 * on overview), smooth area lines (bars for a single bucket), legend INSIDE the card above the canvas,
 * axes as in the shared LineChart on overview. The sections own the title row, the card and the
 * loading/error/empty states — this component only maps series → ECharts.
 */
const InsightsTrendChart: React.FC<InsightsTrendChartProps> = ({ buckets, period, series }) => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();

  // Date labels exactly as on the overview charts (Brand Position, SoV over time): the shared
  // locale-aware formatters — compact axis label per bucket, full date with the year in the tooltip.
  const dateLabels = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  // The CSS chart tokens are read from the DOM (canvas cannot use CSS vars), so they are re-read per
  // theme; `theme` sits in the memoized value to be a real dependency for exhaustive-deps.
  const chartColors = useMemo(() => ({ ...getChartThemeColors(), theme }), [theme]);

  const chartOptions: EChartsOption = useMemo(() => {
    // A single bucket → bars: a line with boundaryGap:false renders a dangling dot at the edge.
    const isSingleBucket = buckets.length === 1;

    const areaGradient = (hex: string, fromAlpha: number, toAlpha = 0) => ({
      type: 'linear' as const,
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        { offset: 0, color: withAlpha(hex, fromAlpha) },
        { offset: 1, color: withAlpha(hex, toAlpha) },
      ],
    });

    const seriesFor = ({ name, data, color, areaAlpha }: InsightsTrendSeries) =>
      isSingleBucket
        ? {
            name,
            type: 'bar' as const,
            data,
            barWidth: 42,
            // Stronger alpha than under the line — a bar has no solid stroke as an anchor; at 0.28→0
            // it was unreadable (the gradient is computed relative to the bar's bbox).
            itemStyle: { color: areaGradient(color, 0.8, 0.3), borderRadius: [2, 2, 0, 0] },
          }
        : {
            name,
            type: 'line' as const,
            data,
            // Smooth curves as in "Citation trends" on Sources (shared LineChart with smooth={true}).
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2, color },
            itemStyle: { color },
            ...(areaAlpha > 0 && { areaStyle: { color: areaGradient(color, areaAlpha) } }),
          };

    // Y axis tightened to the data as in "Brand Score & Visibility" on overview: max = highest plotted
    // value + 10, capped at 100 — a fixed 0–100 flattened low-percentage series into a line at the bottom.
    const highest = series.reduce(
      (acc, item) => item.data.reduce<number>((inner, value) => Math.max(inner, value ?? 0), acc),
      0
    );
    const yAxisMax = Math.min(100, Math.ceil(highest) + 10);

    return {
      grid: { top: 16, left: 40, right: 16, bottom: 24 },
      // Axes as in the shared LineChart ("Brand Score & Visibility" on overview): both axis lines in
      // chartColors.axisLine, X labels 12px / Y labels 10px in chartColors.axisLabel.
      xAxis: {
        type: 'category',
        // With boundaryGap:false a bar is centered on the axis edge and half cut off.
        boundaryGap: isSingleBucket,
        data: buckets.map((bucket) => dateLabels.axis(bucket.date)),
        axisLine: { onZero: false, lineStyle: { color: chartColors.axisLine } },
        axisTick: { show: false },
        axisLabel: { color: chartColors.axisLabel },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: yAxisMax,
        axisLine: { show: true, onZero: false, lineStyle: { color: chartColors.axisLine } },
        axisLabel: { color: chartColors.axisLabel, fontSize: 10, formatter: '{value}%' },
        splitLine: { lineStyle: { color: chartColors.splitLine } },
      },
      tooltip: {
        trigger: 'axis',
        ...(isSingleBucket && { axisPointer: { type: 'shadow' as const } }),
        borderRadius: 8,
        padding: [5, 10],
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        textStyle: { color: chartColors.tooltipText, fontSize: 12, lineHeight: 22 },
        formatter: (params: any) => {
          // param.marker inherits the color from itemStyle — for a gradient bar ECharts takes the first
          // colorStop (rgba with alpha), so we build the marker ourselves with the series' solid color.
          const solidMarker = (hex: string) =>
            `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${hex};"></span>`;
          const list = Array.isArray(params) ? params : [params];
          const rawDate = buckets[list[0]?.dataIndex]?.date;
          const header = rawDate ? `<strong>${dateLabels.tooltip(rawDate)}</strong>` : '';
          const rows = list
            .filter((param: any) => param.value !== null && param.value !== undefined)
            .map(
              (param) => `
                <div class="chart-tooltip__row">
                  <div class="chart-tooltip__marker">${solidMarker(
                    series[param.seriesIndex]?.color ?? chartColors.axisLabel
                  )}</div>
                  <div class="chart-tooltip__text"><span>${escapeHtml(param.seriesName)}</span></div>
                  <div><strong>${param.value}%</strong></div>
                </div>`
            );
          return `<div class="chart-tooltip__content">${header}${rows.join('')}</div>`;
        },
      },
      series: series.map(seriesFor),
    };
  }, [buckets, series, chartColors, dateLabels]);

  return (
    <>
      {/* Series legend INSIDE the frame, above the chart — like the LineChart legend in "Brand Score &
          Visibility" on overview (there LineChart renders it inside .stats-box__content). */}
      <div className="insights-trend-chart__legend">
        {series.map((item) => (
          <span key={item.name} className="insights-trend-chart__legend-item">
            <span className="insights-trend-chart__legend-dot" style={{ backgroundColor: item.color }} />
            {item.name}
          </span>
        ))}
      </div>
      {/* notMerge like the other panel charts: with the default merge a series that disappears (Cited
          when the brand's domains become unknown) would stay on the canvas and in the tooltip. */}
      <ReactECharts
        key={theme}
        option={chartOptions}
        notMerge={true}
        style={{ height: '280px', width: '100%' }}
      />
    </>
  );
};

export default InsightsTrendChart;
